import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CListGroup,
  CListGroupItem,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CProgress,
  CRow,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload, cilX } from "@coreui/icons";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import { triggerDownloadFromUrl } from "views/applications/_utils/applicationPdfExport";

// mesma regra de expiração de download do Kickoff (ver
// KickoffDeckList.js), mas com janela de 48h — `download_expirado` na
// resposta da API é a fonte de verdade, isto aqui só evita esperar por um
// novo pedido para esconder o botão.
const DOWNLOAD_DISPONIVEL_HORAS = 48;

const isDownloadExpirado = (job, now) => {
  if (job.download_expirado !== undefined) return job.download_expirado;
  if (!job.created_at) return false;
  const criado = new Date(job.created_at).getTime();
  return now - criado > DOWNLOAD_DISPONIVEL_HORAS * 60 * 60 * 1000;
};

const STATUS_BADGE = {
  PENDING: { color: "secondary", label: "Em fila" },
  IN_PROGRESS: { color: "info", label: "A processar" },
  COMPLETED: { color: "success", label: "Concluído" },
  FAILED: { color: "danger", label: "Falhou" },
};

const ORIGEM_LABEL = {
  MANUAL: "Pesquisa individual",
  EXCEL: "Upload em lote",
};

const CompanyFundingList = () => {
  const [toast, setToast] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [origem, setOrigem] = useState("MANUAL");
  const [pesquisa, setPesquisa] = useState("");
  const [ficheiro, setFicheiro] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [previewJob, setPreviewJob] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const fileInputRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchJobs = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await apiClient.get("/company-funding/");
      setJobs(response.data || []);
      return response.data || [];
    } catch (error) {
      if (!silent) {
        setToast({
          title: "Projetos Financiados",
          message: "Erro a carregar o histórico de pesquisas",
          type: "error",
        });
      }
      return [];
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchJobs().finally(() => setIsLoading(false));
  }, [fetchJobs]);

  // poll enquanto houver algum job PENDING/IN_PROGRESS — o cruzamento com o
  // Portal Mais Transparência é assíncrono (fila Celery dedicada
  // "company_funding") para não bloquear o pedido, sobretudo em lotes com
  // várias dezenas de empresas. Um único GET à lista cobre todos os jobs.
  useEffect(() => {
    const temPendentes = jobs.some((j) =>
      ["PENDING", "IN_PROGRESS"].includes(j.status),
    );
    if (!temPendentes) return undefined;

    const timer = setInterval(() => {
      fetchJobs({ silent: true });
    }, 3000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  const resetForm = () => {
    setPesquisa("");
    setFicheiro(null);
    setCandidatos([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submeter = async (formData) => {
    setSubmitting(true);
    try {
      const response = await apiClient.post("/company-funding/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.candidatos) {
        setCandidatos(response.data.candidatos);
        setToast({
          title: "Projetos Financiados",
          message:
            "Foi encontrada mais que uma empresa com esse nome — escolhe a certa.",
          type: "warning",
        });
        return;
      }

      setJobs((prev) => [response.data, ...prev]);
      resetForm();
      setToast({
        title: "Projetos Financiados",
        message: "Pesquisa iniciada — acompanha o estado na tabela.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Projetos Financiados",
        message:
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Erro ao iniciar a pesquisa.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (origem === "EXCEL") {
      if (!ficheiro) {
        setToast({
          title: "Projetos Financiados",
          message: "Escolhe o ficheiro Excel com as colunas Empresa e NIF.",
          type: "warning",
        });
        return;
      }
      const formData = new FormData();
      formData.append("origem", "EXCEL");
      formData.append("ficheiro", ficheiro);
      await submeter(formData);
      return;
    }

    if (!pesquisa.trim()) {
      setToast({
        title: "Projetos Financiados",
        message: "Indica o NIF ou o nome da empresa.",
        type: "warning",
      });
      return;
    }

    const soDigitos = pesquisa.replace(/\D/g, "");
    const formData = new FormData();
    formData.append("origem", "MANUAL");
    if (soDigitos.length === 9) {
      formData.append("nif", soDigitos);
    } else {
      formData.append("nome", pesquisa.trim());
    }
    await submeter(formData);
  };

  const escolherCandidato = async (candidato) => {
    const formData = new FormData();
    formData.append("origem", "MANUAL");
    formData.append("nif", candidato.nif);
    formData.append("nome", candidato.nome);
    await submeter(formData);
  };

  const handleDownload = async (job) => {
    try {
      const response = await apiClient.get(`/company-funding/${job.id}/`);
      if (response.data.download_url) {
        await triggerDownloadFromUrl(
          response.data.download_url,
          response.data.output_filename || job.output_filename,
        );
      }
    } catch (error) {
      setToast({
        title: "Projetos Financiados",
        message: "Erro a obter o link de download.",
        type: "error",
      });
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("pt-PT");
  };

  const empresasLabel = (row) => {
    if (row.origem === "MANUAL") {
      const empresa = (row.empresas_input || [])[0];
      return empresa?.empresa || empresa?.nif_original || "-";
    }
    return `${row.total_empresas} empresa(s)`;
  };

  const columns = [
    {
      name: "Data",
      selector: (row) => formatDateTime(row.created_at),
      sortable: true,
    },
    {
      name: "Origem",
      selector: (row) => ORIGEM_LABEL[row.origem] || row.origem,
      sortable: true,
    },
    {
      name: "Empresa(s)",
      selector: (row) => empresasLabel(row),
      sortable: true,
      grow: 2,
    },
    {
      name: "Estado",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const badge = STATUS_BADGE[row.status] || {
          color: "secondary",
          label: row.status,
        };
        return (
          <CBadge color={badge.color}>
            {["PENDING", "IN_PROGRESS"].includes(row.status) && (
              <CSpinner size="sm" className="me-1" />
            )}
            {badge.label}
          </CBadge>
        );
      },
    },
    {
      name: "Progresso",
      cell: (row) => {
        if (row.total_empresas <= 1) return null;
        const percentagem = Math.round(
          (100 * (row.empresas_processadas || 0)) / row.total_empresas,
        );
        return (
          <div style={{ minWidth: "140px" }}>
            <CProgress thin value={percentagem} className="mb-1" />
            <div className="small text-medium-emphasis">
              {row.empresas_processadas || 0}/{row.total_empresas}
            </div>
          </div>
        );
      },
    },
    {
      name: "Criado por",
      selector: (row) => row.created_by_name || "-",
      sortable: true,
    },
    {
      name: "",
      button: true,
      cell: (row) => {
        if (row.status !== "COMPLETED" || isDownloadExpirado(row, now)) {
          return null;
        }
        return (
          <CButton
            color="primary"
            size="sm"
            className="text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(row);
            }}
          >
            <CIcon icon={cilCloudDownload} className="me-1" />
            Descarregar
          </CButton>
        );
      },
    },
  ];

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard className="mb-4">
        <CCardHeader>
          <h3 className="mb-0">Projetos Financiados</h3>
          <div className="text-medium-emphasis small">
            Cruza empresas com projetos financiados por fundos europeus
            (PT2020, PT2030 e PRR) via Portal Mais Transparência, com
            indicação de tamanho e associação.
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <CRow className="g-3">
              <CCol md={4}>
                <CFormLabel>Tipo de pesquisa</CFormLabel>
                <CFormSelect
                  value={origem}
                  onChange={(e) => {
                    setOrigem(e.target.value);
                    setCandidatos([]);
                  }}
                >
                  <option value="MANUAL">Pesquisar uma empresa</option>
                  <option value="EXCEL">Carregar Excel (várias empresas)</option>
                </CFormSelect>
              </CCol>

              {origem === "MANUAL" ? (
                <CCol md={8}>
                  <CFormLabel>NIF ou nome da empresa</CFormLabel>
                  <CFormInput
                    value={pesquisa}
                    onChange={(e) => setPesquisa(e.target.value)}
                    placeholder="Ex.: 509248853 ou Streamvalue Consulting Lda"
                  />
                </CCol>
              ) : (
                <CCol md={8}>
                  <CFormLabel>Excel (colunas Empresa e NIF)</CFormLabel>
                  <CFormInput
                    type="file"
                    accept=".xlsx"
                    ref={fileInputRef}
                    onChange={(e) => setFicheiro(e.target.files?.[0] || null)}
                    required
                  />
                </CCol>
              )}

              {candidatos.length > 0 && (
                <CCol xs={12}>
                  <div className="text-medium-emphasis small mb-2">
                    Mais que uma empresa encontrada com esse nome — escolhe a
                    certa:
                  </div>
                  <CListGroup>
                    {candidatos.map((candidato) => (
                      <CListGroupItem
                        key={candidato.nif}
                        component="button"
                        type="button"
                        onClick={() => escolherCandidato(candidato)}
                        className="d-flex justify-content-between align-items-center"
                      >
                        {candidato.nome}
                        <CBadge color="secondary">{candidato.nif}</CBadge>
                      </CListGroupItem>
                    ))}
                  </CListGroup>
                </CCol>
              )}

              <CCol xs={12}>
                <CButton
                  color="info"
                  className="text-white"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting && <CSpinner size="sm" className="me-2" />}
                  Pesquisar
                </CButton>
              </CCol>
            </CRow>
          </CForm>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <h5 className="mb-0">Histórico</h5>
        </CCardHeader>
        <CCardBody>
          {isLoading ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataTableComponent
              columns={columns}
              data={jobs}
              onRowClicked={(row) => setPreviewJob(row)}
            />
          )}
        </CCardBody>
      </CCard>

      <COffcanvas
        placement="end"
        visible={Boolean(previewJob)}
        onHide={() => setPreviewJob(null)}
        style={{ width: "480px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle>Pesquisa #{previewJob?.id}</COffcanvasTitle>
          <CButton
            color="link"
            className="text-body p-0"
            onClick={() => setPreviewJob(null)}
          >
            <CIcon icon={cilX} size="lg" />
          </CButton>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewJob && (
            <div className="d-flex flex-column gap-3">
              {previewJob.status === "FAILED" && previewJob.error && (
                <div className="border rounded-3 p-3 bg-danger-subtle">
                  <div className="text-uppercase text-medium-emphasis small">
                    Erro
                  </div>
                  <div className="fw-semibold">{previewJob.error}</div>
                </div>
              )}

              {(previewJob.avisos || []).length > 0 && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Avisos
                  </div>
                  <ul className="mb-0 mt-2">
                    {previewJob.avisos.map((aviso, idx) => (
                      <li key={idx}>{aviso}</li>
                    ))}
                  </ul>
                </div>
              )}

              {previewJob.status === "COMPLETED" &&
                !isDownloadExpirado(previewJob, now) && (
                  <CButton
                    color="primary"
                    className="text-white"
                    onClick={() => handleDownload(previewJob)}
                  >
                    <CIcon icon={cilCloudDownload} className="me-1" />
                    Descarregar
                  </CButton>
                )}
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default CompanyFundingList;
